import MaterialTable, { Column, MTableBody } from 'material-table';
import React, { useEffect, useMemo, useState } from 'react';
import { getStickyColumns, localization, modifyDefaultOptions, sortByAlphaNum } from '../../../helpers/table';
import useAPI from '../../../hooks/useAPI';
import LoadingTable from '../../../components/table/LoadingTable';
import { TableCell, TableFooter } from '@material-ui/core';
import zIndex from '@material-ui/core/styles/zIndex';

interface StrategiesBudgetTableProps{
  financingType: 'fb' | 'sf',
  year: number
}

interface StrategiesBudgetRow {
  id: number,
  num: number
  name: string,
  headName: string,
  stratPlan: number
  projPlan: number
  approvedSum: number
  acceptedSum: number
  contractSum: number
  paymentSum: number
  approvedPercent: number
  acceptedPercent: number
  contractPercent: number
  paymentPercent: number
}
interface StrategiesBudgetColumn extends Column<StrategiesBudgetRow>{
  aggField?: boolean,
  percentAggField?: boolean,
  field: keyof StrategiesBudgetRow
}

const numberFormatter = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function StrategiesBudgetTable({ financingType, year }: StrategiesBudgetTableProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<StrategiesBudgetRow[]>([]);
  const api = useAPI();

  const columns = useMemo(() => [
    ...getStickyColumns([
      {
      field: 'num',
      title: '№',
      cellStyle: { minWidth: 100 },
      width: 100,
      customSort: (a, b) => sortByAlphaNum(a.num, b.num),
    },
    {
      field: 'name',
      title: 'Стратегический проект / Цель',
      cellStyle: { minWidth: 280 },
      width: 280,
    },
    {
      field: 'headName',
      title: 'Руководитель',
      cellStyle: { minWidth: 200 },
      width: 200,
      render: (rowData) => rowData.headName || '-'
    },
    {
      field: 'stratPlan',
      title: 'План СП / Цели, руб.',
      hidden: financingType === 'sf',
      render: (rowData) => rowData.stratPlan ? numberFormatter.format(rowData.stratPlan) : '-',
      width: 135,
      cellStyle: { minWidth: 135 },
      aggField: true
    },
    {
      field: 'projPlan',
      title: 'Утверждено проектов, руб.',
      render: (rowData) => numberFormatter.format(rowData.projPlan),
      width: 135,
      cellStyle: { minWidth: 135 },
      aggField: true
    },
    {
      field: 'approvedSum',
      title: 'Утверждено закупок, руб.',
      render: (rowData) => numberFormatter.format(rowData.approvedSum),
      width: 135,
      cellStyle: { minWidth: 135 },
      aggField: true
    }].filter(col => !col.hidden), 0),
    {
      field: 'approvedPercent',
      title: '% утверждено',
      render: (rowData) => numberFormatter.format(rowData.approvedPercent * 100),
      percentAggField: true,
    },
    {
      field: 'acceptedSum',
      title: 'Акцепт, руб.',
      render: (rowData) => numberFormatter.format(rowData.acceptedSum),
      aggField: true
    },
    {
      field: 'acceptedPercent',
      title: '% акцепта',
      render: (rowData) => numberFormatter.format(rowData.acceptedPercent * 100),
      percentAggField: true,
    },
    {
      field: 'contractSum',
      title: 'Контрактация, руб.',
      render: (rowData) => numberFormatter.format(rowData.contractSum),
      aggField: true
    },
    {
      field: 'contractPercent',
      title: '% контрактации',
      render: (rowData) => numberFormatter.format(rowData.contractPercent * 100),
      percentAggField: true,
    },
    {
      field: 'paymentSum',
      title: 'Оплата, руб.',
      render: (rowData) => numberFormatter.format(rowData.paymentSum),
      aggField: true
    },
    {
      field: 'paymentPercent',
      title: '% оплаты',
      render: (rowData) => numberFormatter.format(rowData.paymentPercent * 100),
      percentAggField: true,
    },
  ] as StrategiesBudgetColumn[], [financingType])

  const totalData = useMemo(() => Object.fromEntries(
    columns
    .filter((column) => column.aggField)
    .map(({field}) => [field, data.reduce((acc, curr) => {
      if(typeof curr[field] === 'number'){
        return acc + curr[field];
      }
      return acc;
    }, 0)])
  ), [columns, data])

  useEffect(() => {
    const getData = async() => {
      setIsLoading(true);
      const response = await api.get(`/reports/budget/byStrategies?year=${year}&type=${financingType}`) as StrategiesBudgetRow[];
      const sortedData = response.sort((a, b) => sortByAlphaNum(a.num, b.num));
      const processedData = sortedData.map(row => ({
        ...row,
        headName: row.headName ? row.headName.split(', ')
          .sort((a, b) => {
            if (a === 'Князев С. Т.') return -1;
            if (b === 'Князев С. Т.') return 1;
            return a.localeCompare(b);
          })
          .join(', ') : '-'
      }));
      setData(processedData);
      setIsLoading(false);
    }
    getData();
  }, [api, financingType, year]);

  return (
    <LoadingTable loadingObject={{ isLoading }} spinnerStyle={{}}>
      <MaterialTable
        data={data}
        columns={columns}
        components={{
          Body: (props) => <>
            <MTableBody {...props}/>
            <TableFooter style={footerStyles}>
              <TableCell colSpan={3} style={{...footerCellStyles, textTransform: 'uppercase', position: 'sticky', left: 0}}>Итого:</TableCell>
              {columns.filter((column) => (column.aggField || column.percentAggField) && !column.hidden).map((column, i) => <TableCell style={
                  i < (financingType === 'sf' ? 2 : 3)
                  ? {...footerCellStyles, position: 'sticky', left: 580 + i * 135, overflow: 'hidden'}
                  : footerCellStyles
                }>
                {
                  numberFormatter.format(column.aggField 
                    ? totalData[column.field] 
                    : totalData[column.field.replace('Percent', 'Sum')] / (financingType === 'fb' ? totalData.stratPlan : totalData.projPlan) * 100
                )}
              </TableCell>)}
            </TableFooter>
          </>
        }}
        localization={localization}
        options={modifyDefaultOptions({
          toolbar: false,
          filtering: false,
          sorting: true,
          maxBodyHeight: 'calc(100vh - 400px)'
        })}
      />
    </LoadingTable>
  );
}

const footerStyles = {position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 100};
const footerCellStyles = {color: 'rgba(0, 0, 0, 0.87)', fontFamily: 'inherit', fontWeight: 700, backgroundColor: 'white'};